import { useQuery } from '@powersync/react';
import { useRouter } from 'expo-router';
import { Settings } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ActiveRunCard } from '@/components/domain/activeRunCard/ActiveRunCard';
import { ActiveRunCardSkeleton } from '@/components/domain/activeRunCard/ActiveRunCardSkeleton';
import { AccountMenu } from '@/components/domain/AccountMenu';
import { NewActionFab } from '@/components/domain/NewActionFab';
import { NewTemplateSheet } from '@/components/domain/NewTemplateSheet';
import { RunRow } from '@/components/domain/RunRow';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { TemplateEmptyState } from '@/components/domain/TemplateEmptyState';
import { TemplateRow } from '@/components/domain/templateRow/TemplateRow';
import { TemplateRowSkeleton } from '@/components/domain/templateRow/TemplateRowSkeleton';
import { UpsellCard } from '@/components/domain/UpsellCard';
import { Bounded } from '@/components/ui/Bounded';
import { BoundedScrollView } from '@/components/ui/BoundedScrollView';
import { BrandMark } from '@/components/ui/BrandMark';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Icon } from '@/components/ui/Icon';
import { ListRow } from '@/components/ui/ListRow';
import { Screen } from '@/components/ui/Screen';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/Text';
import { useAuth } from '@/lib/auth';
import {
  BILLING_ENABLED,
  FREE_RUN_LIMIT,
  FREE_TEMPLATE_LIMIT,
  useEntitlement,
  useUsage,
} from '@/lib/billing';
import { type Instance } from '@/lib/data';
import { emailLocalPart, timeAgo } from '@/lib/format';
import { useHasSynced, usePendingInstanceIds } from '@/lib/sync-status';
import { useDelayedTrue } from '@/lib/use-delayed-true';
import { useStartRun } from '@/lib/useStartRun';

// Open the start-a-run sheet at roughly half-height so a long template list
// stays compact; gorhom expands it to the content height (capped in Sheet) as
// the user drags or scrolls. Module-scoped so the reference is stable.
const RUN_SHEET_SNAP_POINTS = ['45%'];

type TemplateRowData = {
  id: string;
  owner_id: string;
  title: string;
  icon: string | null;
  step_count: number;
  last_run_at: string | null;
};

type ActiveRunData = {
  id: string;
  owner_id: string;
  title: string;
  started_at: string;
  done: number;
  total: number;
};

export default function HomeScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();
  const { session, signOut } = useAuth();
  const userId = session?.user.id;

  const { data: templates, isLoading: templatesLoading } = useQuery<TemplateRowData>(
    `SELECT t.id, t.owner_id, t.title, t.icon,
        (SELECT COUNT(*) FROM template_items ti WHERE ti.template_id = t.id) AS step_count,
        (SELECT MAX(i.started_at) FROM instances i WHERE i.template_id = t.id) AS last_run_at
      FROM templates t
      WHERE t.deleted_at IS NULL
      ORDER BY MAX(COALESCE(last_run_at, t.updated_at), t.updated_at) DESC
      LIMIT 5`,
  );
  const { data: templatesCount } = useQuery<{ n: number }>(
    `SELECT COUNT(*) AS n FROM templates WHERE deleted_at IS NULL`,
  );
  const { data: activeRuns, isLoading: runsLoading } = useQuery<ActiveRunData>(
    `SELECT i.id, i.owner_id, i.title, i.started_at,
        (SELECT COUNT(*) FROM instance_items ii WHERE ii.instance_id = i.id) AS total,
        (SELECT COUNT(*) FROM instance_items ii WHERE ii.instance_id = i.id AND ii.checked = 1) AS done
      FROM instances i
      WHERE i.status = 'active'
      ORDER BY i.started_at DESC`,
  );
  const { data: history } = useQuery<Instance>(
    `SELECT * FROM instances
      WHERE status = 'completed' ORDER BY completed_at DESC LIMIT 3`,
  );
  const { data: historyCount } = useQuery<{ n: number }>(
    `SELECT COUNT(*) AS n FROM instances WHERE status = 'completed'`,
  );
  const { data: profiles } = useQuery<{ display_name: string | null }>(
    'SELECT display_name FROM profiles WHERE id = ?',
    [userId ?? ''],
  );

  const email = session?.user.email ?? '';
  const displayName = profiles[0]?.display_name ?? emailLocalPart(email) ?? 'You';

  const { isPro } = useEntitlement();
  const usage = useUsage();
  const pendingIds = usePendingInstanceIds();
  const hasSynced = useHasSynced();

  const [runSheetOpen, setRunSheetOpen] = useState(false);
  const [newSheetOpen, setNewSheetOpen] = useState(false);
  const { startRun, starting, error: startError } = useStartRun();

  function newTemplate() {
    if (usage.atTemplateLimit) {
      router.push('/paywall');
      return;
    }
    setRunSheetOpen(false);
    setNewSheetOpen(true);
  }

  async function beginRun(templateId: string) {
    // Keep the sheet open until the run actually starts, so a failure (or the
    // paywall redirect) leaves the user somewhere to see the error / retry rather
    // than dropping them back on Home with no feedback.
    const started = await startRun(templateId);
    if (started) setRunSheetOpen(false);
  }

  const coldStart = templates.length === 0 && activeRuns.length === 0;

  // Confirm a truly-empty account before showing the "No checklists yet" state.
  // When the first sync lands, PowerSync commits the rows and re-fires the watched
  // queries as a separate update from `hasSynced`; if the status flips a frame
  // ahead of the queries repopulating, `coldStart` is momentarily still true and
  // the empty state flashes. Only believe "synced + settled + empty" once it has
  // held briefly, so that gap rides through as skeletons instead.
  const showEmpty = useDelayedTrue(
    hasSynced && !templatesLoading && !runsLoading && coldStart,
    200,
  );
  const initialLoading = templatesLoading || runsLoading || (coldStart && !showEmpty);

  return (
    <Screen edges={['top']}>
      {/* Top bar */}
      <Bounded className="flex-row items-center justify-between px-5 py-2">
        <BrandMark />
        <AccountMenu
          name={displayName}
          email={email}
          plan={BILLING_ENABLED ? (isPro ? 'Pro' : 'Free') : undefined}
          settingsHref="/settings"
          manageSubscriptionHref={BILLING_ENABLED && !isPro ? '/paywall' : undefined}
          onSignOut={signOut}
        >
          <Pressable hitSlop={8} className="p-1">
            <Icon icon={Settings} />
          </Pressable>
        </AccountMenu>
      </Bounded>

      <SyncStatusArea scrollRef={syncScrollRef}>
        {initialLoading ? (
          <BoundedScrollView
            scrollEnabled={false}
            contentContainerClassName="gap-6 px-5 pt-2"
            contentContainerStyle={{ paddingBottom: insets.bottom + 40 }}
          >
            <ActiveRunCardSkeleton />
            <View className="gap-3">
              <TemplateRowSkeleton />
              <TemplateRowSkeleton />
              <TemplateRowSkeleton />
            </View>
          </BoundedScrollView>
        ) : coldStart ? (
          <Bounded className="flex-1 justify-center">
            <EmptyState
              icon="🗒️"
              title="No templates yet"
              body="A template is a reusable checklist — the things you check off, in the order you do them. Build your first one and run it as many times as you like."
              action={{
                label: 'Build your first template',
                onPress: newTemplate,
              }}
              hint="Got a link from someone? Open it to join their run."
            />
          </Bounded>
        ) : (
          <View className="flex-1">
            <BoundedScrollView
              scrollRef={syncScrollRef}
              alwaysBounceVertical
              contentContainerClassName="gap-6 px-5 pt-2"
              contentContainerStyle={{ paddingBottom: insets.bottom + 120 }}
            >
              {/* Active runs */}
              <View className="gap-3">
                <SectionHeader label="Active runs" count={activeRuns.length} />
                {activeRuns.length === 0 ? (
                  <View className="items-center rounded-2xl border border-dashed border-border-strong px-4 py-6">
                    <Text variant="bodyMedium" className="text-muted">
                      Nothing running right now
                    </Text>
                    <Text variant="caption" className="mt-1 text-center">
                      Start a run from one of your templates below.
                    </Text>
                  </View>
                ) : (
                  activeRuns.map((run) => (
                    <ActiveRunCard
                      key={run.id}
                      title={run.title}
                      topLeft={run.owner_id === userId ? 'Your run' : 'Shared with you'}
                      topRight={timeAgo(run.started_at)}
                      statusLine={`${run.done} of ${run.total} steps complete`}
                      done={run.done}
                      total={run.total}
                      href={`/instance/${run.id}`}
                    />
                  ))
                )}
              </View>

              {/* Templates */}
              <View className="gap-3">
                <SectionHeader
                  label={
                    userId && templates.length === 0 ? 'Your templates' : 'Templates'
                  }
                  count={templatesCount[0]?.n ?? templates.length}
                  actionLabel={templates.length > 0 ? 'View all' : undefined}
                  actionIconRight="›"
                  actionHref="/templates"
                />
                {templates.length === 0 ? (
                  <TemplateEmptyState onNewTemplate={newTemplate} />
                ) : (
                  templates.map((t) => (
                    <TemplateRow
                      key={t.id}
                      icon={t.icon}
                      title={t.title}
                      stepCount={t.step_count}
                      meta={t.last_run_at ? `run ${timeAgo(t.last_run_at)}` : 'never run'}
                      shared={t.owner_id !== userId}
                      href={`/template/${t.id}`}
                    />
                  ))
                )}
              </View>

              {/* Free-plan upsell */}
              {BILLING_ENABLED && !isPro && templates.length > 0 ? (
                <UpsellCard
                  checklistsUsed={usage.templates}
                  checklistsLimit={FREE_TEMPLATE_LIMIT}
                  runsUsed={usage.runs}
                  runsLimit={FREE_RUN_LIMIT}
                  href="/paywall"
                />
              ) : null}

              {/* History */}
              {history.length > 0 ? (
                <View className="gap-3">
                  <SectionHeader
                    label="History"
                    count={historyCount[0]?.n ?? history.length}
                    actionLabel="View all"
                    actionIconRight="›"
                    actionHref="/runs"
                  />
                  <View className="overflow-hidden rounded-2xl border border-border bg-card">
                    {history.map((h, i) => (
                      <View key={h.id}>
                        {i > 0 ? <View className="ml-4 h-px bg-border" /> : null}
                        <RunRow
                          instance={h}
                          href={`/instance/${h.id}`}
                          unsynced={pendingIds.has(h.id)}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}
            </BoundedScrollView>

            <View
              pointerEvents="box-none"
              className="absolute inset-x-0 items-center"
              style={{ bottom: insets.bottom ? insets.bottom + 8 : 24 }}
            >
              <NewActionFab
                onStartRun={() => setRunSheetOpen(true)}
                onNewTemplate={newTemplate}
              />
            </View>
          </View>
        )}
      </SyncStatusArea>

      {/* Start-a-run sheet */}
      <Sheet
        visible={runSheetOpen}
        onClose={() => setRunSheetOpen(false)}
        title="Start a run"
        snapPoints={RUN_SHEET_SNAP_POINTS}
      >
        <Text variant="caption" className="mb-3">
          Pick a template to begin
        </Text>
        {startError ? (
          <Text variant="caption" className="mb-3 text-alert">
            {startError}
          </Text>
        ) : null}
        {templates.length === 0 ? (
          <Button block label="Build your first template" onPress={newTemplate} />
        ) : (
          <View className="gap-1">
            {templates.map((t) => {
              const empty = t.step_count === 0;
              return (
                <ListRow
                  key={t.id}
                  leading={
                    <View className="h-9 w-9 items-center justify-center rounded-lg bg-well">
                      <Text className="text-xl">{t.icon ?? '📋'}</Text>
                    </View>
                  }
                  title={t.title}
                  subtitle={
                    empty
                      ? 'Add steps to run'
                      : `${t.step_count} step${t.step_count === 1 ? '' : 's'}`
                  }
                  disabled={empty || starting}
                  onPress={empty || starting ? undefined : () => beginRun(t.id)}
                />
              );
            })}
          </View>
        )}
      </Sheet>

      {/* New-template sheet */}
      <NewTemplateSheet visible={newSheetOpen} onClose={() => setNewSheetOpen(false)} />
    </Screen>
  );
}
